import { Combobox, type ComboboxItem, type OptionValue } from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getKmEntityTitle, useKmEntities, useKmEntitiesByIds } from '@/entities/ontology';

/** Сколько совпадений предлагать в подсказке: список набирается поиском, а не листается */
const SUGGESTIONS_LIMIT = 20;

type Props = {
  /** Выбранные сущности (идентификаторы) */
  value: number[];
  onChange: (value: string[]) => void;
};

/**
 * Фильтр по связанным сущностям: варианты подбираются поиском по названию, а уже выбранные
 * сущности показываются названиями — по ссылке в адресе есть только их идентификаторы.
 */
export const KmRelatedEntitiesFilter: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation('kmEntities');
  const [query, setQuery] = useState('');

  const nameQuery = query.trim();
  const { data: foundPage, isFetching } = useKmEntities(
    { nameQuery, includeDeleted: false, limit: SUGGESTIONS_LIMIT },
    { enabled: Boolean(nameQuery) },
  );
  const selectedEntities = useKmEntitiesByIds(value);

  const options = useMemo<ComboboxItem[]>(() => {
    const selectedOptions = selectedEntities.map((entity) => ({
      value: entity.id,
      label: getKmEntityTitle(entity),
    }));
    const selectedIds = new Set(selectedOptions.map((option) => option.value));

    const foundOptions = (foundPage?.items ?? [])
      .filter((entity) => !selectedIds.has(entity.id))
      .map((entity) => ({ value: entity.id, label: getKmEntityTitle(entity) }));

    return [...selectedOptions, ...foundOptions];
  }, [selectedEntities, foundPage]);

  const handleChange = (values: OptionValue[]) => onChange(values.map(String));

  return (
    <Combobox
      isMulti
      className="sm:w-56"
      options={options}
      value={value}
      onChange={handleChange}
      onSearch={setQuery}
      isLoading={isFetching}
      placeholder={t('relatedEntities')}
      searchPlaceholder={t('relatedEntitiesSearchPlaceholder')}
    />
  );
};
